import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { apiFetch } from "./sdk"

/**
 * Staff notifications (backend lib/staff-alerts.ts): the bell, push notifications on this device,
 * and personal preferences. Push uses the browser's Push API with the service worker in
 * public/terify-sw.js; each browser a person turns it on in becomes one of their devices.
 */

export type AlertPriority = "critical" | "high" | "medium"
export type StaffAlert = {
  id: string
  event: string
  label: string
  priority: AlertPriority
  category: string
  title: string
  body: string | null
  url: string | null
  resource_type?: string | null
  resource_id?: string | null
  data?: Record<string, unknown> | null
  actor_label: string | null
  created_at: string
  read_at: string | null
  push_status: string | null
  email_status: string | null
}
export type AlertTab = "all" | "customers" | "orders" | "security"
export type AlertsResp = { alerts: StaffAlert[]; unread: number; unread_critical: number; unread_by_tab?: Partial<Record<AlertTab, number>> }
export type AlertPrefs = { enabled: boolean; push: { high: boolean; medium: boolean }; email: { high: boolean; medium: boolean }; muted: string[] }
export type AlertDevice = { id: string; label: string | null; user_agent: string | null; created_at: string; last_success_at: string | null; last_error: string | null; failures: number; endpoint_hash: string }
export type AlertSettings = { prefs: AlertPrefs; devices: AlertDevice[]; public_key: string; role: string | null; events: { key: string; priority: AlertPriority; category: string; label: string; email?: boolean }[] }
export type TestResult = { push: { device_id: string; label: string | null; ok: boolean; error?: string }[]; push_status: string; email: string }

export function useAlerts(enabled = true, tab: AlertTab = "all") {
  const qc = useQueryClient()
  // A push arriving while the admin is open refreshes the bell at once.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return
    const h = (e: MessageEvent) => {
      if (e.data?.type === "terify-alert") qc.invalidateQueries({ queryKey: ["staff-alerts"] })
    }
    navigator.serviceWorker.addEventListener("message", h)
    return () => navigator.serviceWorker.removeEventListener("message", h)
  }, [qc])
  return useQuery({
    queryKey: ["staff-alerts", "list", tab],
    enabled,
    queryFn: () => apiFetch<AlertsResp>(`/admin/terify/me/alerts?limit=40${tab === "all" ? "" : `&tab=${tab}`}`),
    refetchInterval: 45_000,
    staleTime: 15_000,
  })
}

/**
 * Where clicking a bell item goes, or null when it has no page of its own (the item then opens in
 * place with its full text). Also mends rows written before 3 Nov: contact messages that pointed at
 * the customer list, data exports without a link, customer changes that pointed at the list.
 */
export function alertTarget(a: Pick<StaffAlert, "event" | "url" | "resource_id" | "data">, can: (p: "logs:read" | "customers:read") => boolean): string | null {
  const url = a.url && a.url.trim() ? a.url : null
  const messageId = typeof a.data?.message_id === "string" ? a.data.message_id : null
  if (a.event === "customer.inquiry") {
    if (!can("customers:read")) return null
    if (url?.startsWith("/messages/")) return url
    if (messageId) return `/messages/${messageId}`
    return null
  }
  if (a.event === "report.exported" && !url) return can("logs:read") ? "/settings/security-log?q=export&range=30d" : null
  if (a.event === "customer.updated" && url === "/customers") return a.resource_id ? `/customers/${a.resource_id}` : null
  if (url?.startsWith("/settings/security-log") && !can("logs:read")) return null
  if ((url?.startsWith("/messages") || url?.startsWith("/customers")) && !can("customers:read")) return null
  return url
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { ids: string[] } | { all: true }) => apiFetch<{ marked: number }>("/admin/terify/me/alerts/read", { method: "POST", body: JSON.stringify(v) }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["staff-alerts"] }),
  })
}

export function useAlertSettings(enabled = true) {
  return useQuery({ queryKey: ["staff-alerts", "settings"], enabled, queryFn: () => apiFetch<AlertSettings>("/admin/terify/me/alerts/settings"), staleTime: 60_000 })
}

export function useSaveAlertPrefs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (prefs: Partial<AlertPrefs>) => apiFetch<AlertSettings>("/admin/terify/me/alerts/settings", { method: "POST", body: JSON.stringify(prefs) }),
    onSuccess: (d) => qc.setQueryData(["staff-alerts", "settings"], d),
  })
}

export function useSendTest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { email?: boolean }) => apiFetch<TestResult>("/admin/terify/me/alerts/send-test", { method: "POST", body: JSON.stringify(v) }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["staff-alerts"] }),
  })
}

// ── this device ──────────────────────────────────────────────────────────────

export type PushSupport = "ok" | "unsupported" | "ios_needs_install" | "insecure"

const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || (navigator as any).standalone === true

export function pushSupport(): PushSupport {
  if (!window.isSecureContext) return "insecure"
  const has = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  if (isIos() && !standalone()) return "ios_needs_install"
  return has ? "ok" : "unsupported"
}

/** "Chrome on Mac", "Safari on iPhone"… so the device list is readable. */
export function deviceLabel() {
  const u = navigator.userAgent
  const browser = /Edg\//.test(u) ? "Edge" : /OPR\//.test(u) ? "Opera" : /CriOS|Chrome\//.test(u) ? "Chrome" : /FxiOS|Firefox\//.test(u) ? "Firefox" : /Safari\//.test(u) ? "Safari" : "Browser"
  const os = /iPhone/.test(u) ? "iPhone" : /iPad/.test(u) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ? "iPad" : /Android/.test(u) ? "Android" : /Mac OS X/.test(u) ? "Mac" : /Windows/.test(u) ? "Windows" : /Linux/.test(u) ? "Linux" : "device"
  return `${browser} on ${os}${standalone() ? " (app)" : ""}`
}

function keyBytes(base64: string) {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4)
  const raw = atob((base64 + pad).replace(/-/g, "+").replace(/_/g, "/"))
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

async function registration() {
  const existing = await navigator.serviceWorker.getRegistration("/")
  if (existing) return existing
  return navigator.serviceWorker.register("/terify-sw.js", { scope: "/" })
}

/** This browser's push subscription, if any. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== "ok") return null
  const reg = await navigator.serviceWorker.getRegistration("/")
  return (await reg?.pushManager.getSubscription()) ?? null
}

/** Asks for permission (must follow a click), subscribes this browser and registers it as one of my devices. */
export async function enablePush(publicKey: string): Promise<"on" | "denied" | "unsupported"> {
  if (pushSupport() !== "ok") return "unsupported"
  const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission()
  if (perm !== "granted") return "denied"
  const reg = await registration()
  await navigator.serviceWorker.ready
  let sub = await reg.pushManager.getSubscription()
  // Subscribed with an older store key (server secret changed): start again with the current one.
  const current = sub?.options?.applicationServerKey ? btoa(String.fromCharCode(...new Uint8Array(sub.options.applicationServerKey as ArrayBuffer))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "") : null
  if (sub && current && current !== publicKey) {
    await sub.unsubscribe().catch(() => {})
    sub = null
  }
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) })
  await apiFetch("/admin/terify/me/alerts/devices", { method: "POST", body: JSON.stringify({ subscription: sub.toJSON(), label: deviceLabel() }) })
  return "on"
}

export async function disablePush() {
  const sub = await currentSubscription()
  if (!sub) return
  await apiFetch("/admin/terify/me/alerts/devices", { method: "POST", body: JSON.stringify({ remove_endpoint: sub.endpoint }) }).catch(() => {})
  await sub.unsubscribe().catch(() => {})
}

/**
 * Keeps this browser's subscription registered: when permission is already granted (turned on
 * earlier, or another account on this browser), re-sends it so the server knows this device
 * belongs to whoever is signed in now. Silent; never prompts.
 */
export async function syncPush(publicKey: string) {
  if (pushSupport() !== "ok" || Notification.permission !== "granted") return false
  try {
    return (await enablePush(publicKey)) === "on"
  } catch {
    return false
  }
}
